import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { API_BASE } from "../config";

export function ParametresAdmin({ user }) {
  const token = user?.token;

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    }),
    [token]
  );

  const [loading, setLoading] = useState(false);
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [toast, setToast] = useState({ type: "", msg: "" });

  const [profile, setProfile] = useState({ email: "" });
  const [form, setForm] = useState({ email: "" });

  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const confirmPwdRef = useRef(null);

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwd, setPwd] = useState({
    mot_de_passe_actuel: "",
    nouveau_mot_de_passe: "",
    confirmation: "",
  });

  const [error, setError] = useState("");

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  const resetMessages = () => setError("");

  const fetchProfil = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/me`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur chargement profil");
      const p = { email: data.item?.email || "" };
      setProfile(p);
      setForm({ ...p });
    } catch (e) {
      showToast("error", e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchProfil();
  }, [fetchProfil]);

  const isDirty = form.email !== profile.email;

  const cancelEdits = () => {
    resetMessages();
    setForm({ ...profile });
    setConfirmSaveOpen(false);
    setConfirmPassword("");
  };

  const validateProfil = () => {
    const email = (form.email || "").trim();
    if (!email) {
      setError("Email requis.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Format d'email invalide.");
      return false;
    }
    return true;
  };

  const requestSave = () => {
    resetMessages();
    if (!isDirty) return;
    if (!validateProfil()) return;
    setConfirmSaveOpen(true);
    setTimeout(() => confirmPwdRef.current?.focus?.(), 50);
  };

  const confirmAndSave = async () => {
    if (!token) return;
    resetMessages();
    if (!validateProfil()) return;

    const pwdValue = (confirmPassword || "").trim();
    if (!pwdValue) {
      setError("Veuillez saisir votre mot de passe pour valider.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/me`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          email: (form.email || "").trim().toLowerCase(),
          mot_de_passe: pwdValue,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur mise à jour");

      showToast("success", "Informations mises à jour");
      setConfirmSaveOpen(false);
      setConfirmPassword("");
      await fetchProfil();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  const closePwd = () => {
    setPwd({ mot_de_passe_actuel: "", nouveau_mot_de_passe: "", confirmation: "" });
    setShowPasswordForm(false);
    resetMessages();
  };

  const validatePwd = () => {
    if (!pwd.mot_de_passe_actuel) {
      setError("Veuillez saisir votre mot de passe actuel.");
      return false;
    }
    if (!pwd.nouveau_mot_de_passe) {
      setError("Veuillez saisir un nouveau mot de passe.");
      return false;
    }
    if (pwd.nouveau_mot_de_passe.length < 8) {
      setError("Mot de passe trop court (min 8 caractères).");
      return false;
    }
    if (pwd.nouveau_mot_de_passe !== pwd.confirmation) {
      setError("La confirmation ne correspond pas.");
      return false;
    }
    return true;
  };

  const changePassword = async () => {
    if (!token) return;
    resetMessages();
    if (!validatePwd()) return;

    setLoadingPwd(true);
    try {
      const res = await fetch(`${API_BASE}/admin/me/password`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          mot_de_passe_actuel: pwd.mot_de_passe_actuel,
          nouveau_mot_de_passe: pwd.nouveau_mot_de_passe,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur changement mot de passe");

      showToast("success", "Mot de passe modifié");
      closePwd();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoadingPwd(false);
    }
  };

  return (
    <div className="adminPanel blocagesCompact collectiviteSettings cabinetCompact">
      {error && <div className="adminError">{error}</div>}

      {/* INFORMATIONS DU COMPTE */}
      <div className="adminCard adminCardSpan">
        <div className="adminCardHeader cabinetCardHeader">
          <div>
            <div className="adminCardTitle">Informations du compte</div>
            <div className="adminCardDesc">Modifiez votre adresse email</div>
          </div>
          <button
            className="btnSecondary"
            onClick={async () => {
              await fetchProfil();
              showToast("success", "Profil à jour");
            }}
            disabled={loading}
          >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>
        </div>

        <div className="cabinetForm">
          <label className="cabinetField">
            <div className="cabinetLabel">Adresse email</div>
            <input
              className="cabinetInput"
              type="email"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              placeholder="admin@formidoc.fr"
              autoComplete="email"
            />
          </label>

          <div className="cabinetActions">
            <button
              className="btnSecondary btnCompact"
              onClick={cancelEdits}
              disabled={loading || !isDirty}
            >
              Annuler
            </button>
            <button
              className="btnPrimary btnCompact"
              onClick={requestSave}
              disabled={loading || !isDirty}
            >
              Enregistrer
            </button>
          </div>

          {confirmSaveOpen && (
            <div className="cabinetConfirmBox">
              <div className="cabinetConfirmTitle">Confirmer votre identité</div>
              <div className="cabinetConfirmSub">
                Pour valider ces modifications, saisissez votre mot de passe.
              </div>

              <label className="cabinetField" style={{ marginTop: 10 }}>
                <div className="cabinetLabel">Mot de passe</div>
                <input
                  ref={confirmPwdRef}
                  className="cabinetInput"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Votre mot de passe actuel"
                  autoComplete="current-password"
                />
              </label>

              <div className="cabinetActions" style={{ marginTop: 10 }}>
                <button
                  className="btnSecondary btnCompact"
                  onClick={() => {
                    setConfirmSaveOpen(false);
                    setConfirmPassword("");
                    resetMessages();
                  }}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button
                  className="btnPrimary btnCompact"
                  onClick={confirmAndSave}
                  disabled={loading}
                >
                  {loading ? "Validation..." : "Valider & enregistrer"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECURITE */}
      <div className="adminCard adminCardSpan">
        <div className="adminCardHeader cabinetCardHeader">
          <div>
            <div className="adminCardTitle">Sécurité</div>
            <div className="adminCardDesc">Changez votre mot de passe</div>
          </div>

          {!showPasswordForm ? (
            <button
              className="btnPrimary btnCompact"
              onClick={() => {
                resetMessages();
                setShowPasswordForm(true);
              }}
            >
              Changer le mot de passe
            </button>
          ) : (
            <button
              className="btnSecondary btnCompact"
              onClick={closePwd}
              disabled={loadingPwd}
            >
              Fermer
            </button>
          )}
        </div>

        <div className={`cabinetCollapse ${showPasswordForm ? "isOpen" : ""}`}>
          <div className="cabinetPwdGrid">
            <label className="cabinetField">
              <div className="cabinetLabel">Mot de passe actuel</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.mot_de_passe_actuel}
                onChange={(e) => setPwd((s) => ({ ...s, mot_de_passe_actuel: e.target.value }))}
                placeholder="Votre mot de passe actuel"
                autoComplete="current-password"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Nouveau mot de passe</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.nouveau_mot_de_passe}
                onChange={(e) =>
                  setPwd((s) => ({ ...s, nouveau_mot_de_passe: e.target.value }))
                }
                placeholder="Minimum 8 caractères"
                autoComplete="new-password"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Confirmation</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.confirmation}
                onChange={(e) => setPwd((s) => ({ ...s, confirmation: e.target.value }))}
                placeholder="Retapez le nouveau mot de passe"
                autoComplete="new-password"
              />
            </label>
          </div>

          <div className="cabinetActions">
            <button
              className="btnSecondary btnCompact"
              onClick={closePwd}
              disabled={loadingPwd}
            >
              Annuler
            </button>
            <button
              className="btnPrimary btnCompact"
              onClick={changePassword}
              disabled={loadingPwd}
            >
              {loadingPwd ? "Modification..." : "Enregistrer"}
            </button>
          </div>

          <div className="cabinetHint">
            Conseil : utilisez au moins 8 caractères, avec lettres et chiffres.
          </div>
        </div>
      </div>

      {toast.msg && (
        <div className={`adminToast ${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}
